<template>
  <v-container fluid fill-height>
    <v-row align="center" justify="center">
      <v-col cols="12">
        <v-card flat class="mx-auto public-form transparent">
          <v-card outlined id="signin">
            <v-card-text class="text-center">
              <v-img :src="$app.opts.authLogoSrc" :alt="$app.opts.title" max-height="250" contain />
            </v-card-text>
            <v-card-text class="text-center title">
              <h3>{{ $t('signin') }}</h3>
            </v-card-text>
            <v-alert v-if="fromInvitation" :value="true" color="info" class="subheading mx-0">
              <div v-html="$t('invitationText')" />
            </v-alert>
            <FormErrors :show="showResourceErrors" :errors="resourceErrors" />
            <v-card-text class="py-2">
              <v-form>
                <v-text-field
                  append-icon="$vuetify.icons.values.user"
                  v-model.trim="form.login"
                  :error-messages="loginErrors"
                  v-bind:label="$t('login')"
                  required
                  @input="$v.form.login.$touch()"
                  @blur="$v.form.login.$touch()"
                  v-on:keyup.enter="doSignin"
                ></v-text-field>
                <v-text-field
                  append-icon="$vuetify.icons.values.password"
                  v-model.trim="form.password"
                  v-bind:label="$t('password')"
                  type="password"
                  required
                  :error-messages="passwordErrors"
                  @input="$v.form.password.$touch()"
                  @blur="$v.form.password.$touch()"
                  v-on:keyup.enter="doSignin"
                ></v-text-field>
              </v-form>
              <div class="text-right">
                <v-btn color="default" text left :to="{ name: 'forgotPassword' }">{{ $t('forgotPassword') }}</v-btn>
              </div>
            </v-card-text>
            <v-card-actions>
              <v-btn block color="accent" @click="doSignin" depressed :loading="loading" :disabled="loading">{{
                $t('signin')
              }}</v-btn>
            </v-card-actions>
            <v-card-text class="py-2 mb-2 text-center">
              {{ $t('noAccount') }}
              <br />
              <v-btn color="default" text :to="registrationLink">{{ $t('register') }}</v-btn>
            </v-card-text>
          </v-card>

          <v-card flat class="my-3 transparent text-center">
            <LanguageField use-buttons v-model="myLocale" immediate fieldClass="elevation-0" />
          </v-card>
        </v-card>
      </v-col>
    </v-row>
    <RefreshApp />
  </v-container>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { NAMESPACE } from '../store'
import { NAMESPACE as ME_NAMESPACE } from '@argon/iam/me/store'
import { required } from 'vuelidate/lib/validators'
import { BaseForm, FormErrors, LanguageField, RefreshApp } from '@argon/app/components'

export default {
  extends: BaseForm,
  components: { FormErrors, LanguageField, RefreshApp },
  validations: {
    form: {
      login: { required },
      password: { required }
    }
  },
  data: () => ({
    form: {
      login: '',
      password: ''
    },
    loading: false
  }),
  computed: {
    ...mapState(NAMESPACE, ['title', 'authenticated']),
    ...mapGetters(ME_NAMESPACE, ['locale']),
    myLocale: {
      get() {
        return this.locale
      },
      set(v) {
        this.setLocale(v)
      }
    },
    loginErrors() {
      const errors = []
      if (!this.$v.form.login.$dirty) return errors
      !this.$v.form.login.required && errors.push(this.$t('required'))
      this.getServerErrors('login', errors)
      return errors
    },
    passwordErrors() {
      const errors = []
      if (!this.$v.form.password.$dirty) return errors
      !this.$v.form.password.required && errors.push(this.$t('required'))
      this.getServerErrors('password', errors)
      return errors
    },
    fromInvitation() {
      return this.$route.query.redirect && this.$route.query.redirect.includes('invitation')
    },
    registrationLink() {
      return { name: 'register', query: this.$route.query }
    },
    redirect() {
      return this.$route.query.redirect || '/'
    }
  },
  methods: {
    ...mapActions(NAMESPACE, ['signin']),
    ...mapActions(ME_NAMESPACE, ['setLocale']),
    doSignin() {
      this.resetServerErrors()
      if (this.$v.form.$invalid) {
        return this.$v.form.$touch()
      }
      this.loading = true
      const payload = {
        login: this.form.login,
        password: this.form.password
      }
      this.signin(payload)
        .then((resp) => {
          this.$router.push({ path: this.redirect })
        })
        .catch(this.setServerErrors)
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style scoped>
.registration-link {
  color: white;
}
</style>

<i18n>
{
  "en": {
    "forgotPassword": "Forgot Password ?",
    "invitationText": "You've been invited to join an <b>organization</b>. If you already have an account <b>sign in</b>, otherwise <b>register</b> a new account.",
    "login": "@:labels.login",
    "noAccount": "Don't have an account?",
    "password": "@:labels.password",
    "register": "@:labels.register",
    "required": "@:labels.required",
    "signin": "@:labels.signin"
  },
  "fr": {
    "forgotPassword": "Perdu votre mot de passe ?",
    "invitationText": "Vous êtes invité(e) à joindre une <b>organisation</b>. Si vous avez déjà un compte <b>connectez-vous</b>, sinon <b>enregistrez</b> un nouveau compte.",
    "login": "@:labels.login",
    "noAccount": "Pas de compte?",
    "password": "@:labels.password",
    "register": "@:labels.register",
    "required": "@:labels.required",
    "signin": "@:labels.signin"
  }
}
</i18n>
